import React, { memo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import useMartyContext from 'hooks/useMartyContext';
import { BEST_FOR_YOU_FACETFIELD, PRICE_RANGE_FACET_NAME } from 'constants/appConstants';
import COLOR_SWATCH_MAP from 'constants/colorSwatchMap';
import { evFacetClick, evGroupedPillToggle } from 'events/search';
import FacetAutoComplete from 'components/common/AutoComplete';
import { NOFOLLOW_FACET_RE } from 'common/regex';
import { capitalize } from 'helpers';
import { trackEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import Tooltip from 'components/common/Tooltip';
import { MoreInfo } from 'components/icons';
import Sort from 'zen/components/search/Sort';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { savedValuesMatch } from 'helpers/SearchUtils';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import PriceSlider from 'components/search/PriceSlider';
import NewSort from 'components/search/NewSort';

import css from 'styles/components/search/newFacets.scss';

export const NewFacets = memo(({ facets, filters, products, mobileFacetSort, facetHeader, hydraCrossSiteSelling, hasAutoComplete, autoComplete, onFacetSelect, filterBrandDesigner, onFacetGroupSelect, isCustomer, onSaveSizeClick, onResetSizeClick, autoCompleteMinValues, onSaveFeatureToggle, hydraNewSearchNav, hydraPills, toggleSizingFacetGroup, toggleShowMore, onSortSelected, makePersonalizedSortToggle }) => {
  const { marketplace: { search: { sortOptions, hasSaveFilters } }, testId } = useMartyContext();

  // By binding handleFacetSelect is executed in the context of the Facets Component
  const handleFacetSelect = (facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isSelected, section, e) => {
    // This is executed in the context of the Search Component because it's bound to it in its constructor function
    onFacetSelect(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, null, section);
    // Track for Amethyst
    track(() => ([
      evFacetClick, {
        facetGroup,
        facetName,
        facetClickSource: 'LEFT_HAND_FACET_NAV',
        selected: !isSelected,
        deselected: !!isSelected
      }
    ]));

    // track if autocomplete used for this group
    if (hasAutoComplete && autoComplete[facetGroup]?.text) {
      trackEvent('TE_SEARCH_AUTOCOMPLETE', `${facetGroup}:${autoComplete[facetGroup].text}`);
    }

    /*
     * This preventDefault stops the link from being followed and instead allows
     * the facet updates to be handled through the state. If you remove this,
     * you'll see multiple search API requests occurring for a single facet
     * click which is not desirable.
     */
    e.preventDefault();
    return false;
  };

  const renderFacetHead = (facetGroup, isToggled, section) => {
    const { facetFieldDisplayName } = facetGroup;
    const displayName = filterBrandDesigner && facetFieldDisplayName === 'Brand' ? 'Designer' : facetFieldDisplayName;
    if (section === 'sizing') {
      return <h4 className={css.facetGroupHead}>{displayName}</h4>;
    }

    return (
      <h3 className={css.facetGroupName}>
        <button
          type="button"
          className={css.facetGroupHead}
          aria-expanded={!isToggled}
          data-selected-facet-group-name={facetGroup.facetField}
          data-selected-facet-section={section}
          data-open={!isToggled}
          data-test-id-facet-head-name={testId(displayName)}
          data-test-id={testId('facetGroupHeadTestId')}
          onClick={onFacetGroupSelect}>
          {displayName}
        </button>
        <span className={css.facetTooltipWrapper}>
          {facetGroup.facetField === BEST_FOR_YOU_FACETFIELD && (
            <Tooltip
              tooltipId="bestForYou"
              wrapperClassName={css.tooltipWrapper}
              tooltipClassName={css.tooltip}
              direction="right"
              content={'We\'re blending your feedback, recent purchases, and shopping behavior to perfect your searches. Hooray!'}>
              <MoreInfo/>
            </Tooltip>
          )}
        </span>
      </h3>
    );
  };

  const makeSaveSizeFeature = (facetField, facetFieldDisplayName) => {
    const { savedsizes, selected } = filters;
    const savedFacets = savedsizes?.filters?.[facetField];
    let content = null;
    if (savedFacets) {
      const hasPossibleSaveValue = selected.multiSelects?.[facetField]?.length;
      const displayName = facetFieldDisplayName.toLowerCase();
      const savedFiltersMatchChosen = savedValuesMatch(savedFacets, selected.multiSelects[facetField]);
      if (isCustomer && savedFacets.length && savedFiltersMatchChosen) {
        const buttonText = `Saved ${displayName}(s): ${savedsizes.filters[facetField].join(' & ')}`;
        content = (
          <>
            <span id="resetDescription">{buttonText}</span>
            <button type="button" aria-describedby="resetDescription" onClick={onResetSizeClick.bind(this, facetField)}>Reset</button>
          </>
        );
      } else if (hasPossibleSaveValue) {
        const buttonText = `Save ${displayName}(s) for future searches?`;
        content = (
          <>
            <span id="saveDescription">{buttonText}</span>
            <button type="button" aria-describedby="saveDescription" onClick={onSaveSizeClick}>Save</button>
          </>
        );
      }

      if (content) {
        return (
          <div className={css.saveFacets}>
            {content}
          </div>
        );
      }
    }
    return null;
  };

  const makeSavedFiltersToggleText = () => {
    const { savedsizes } = filters;
    const savedKeys = Object.keys(savedsizes.filters);
    const message = [];
    for (const section of Object.keys(facets.navigation)) {
      for (const facet of facets.navigation[section]) {
        const { facetFieldDisplayName, facetField } = facet;
        const hasSavedSizes = savedsizes.filters[facetField]?.length;
        const isSavedSizeFacetGroup = savedKeys.includes(facetField);
        if (isSavedSizeFacetGroup && hasSavedSizes) {
          message.push(`\n${facetFieldDisplayName.toLowerCase()}(s): ${savedsizes.filters[facetField].join(', ')}`);
        }
      }
    }

    return ` for ${message.join(' and ')}`;
  };

  const makeSavedFiltersTop = () => {
    const { savedsizes, applySavedFilters, selected } = filters;
    if (savedsizes) {
      const savedSizes = Object.values(savedsizes.filters);
      const hasSavedValues = savedSizes.some(v => v.length);
      const showSavedToggle = savedSizes.length;
      const hasChosenASaveFilter = Object.keys(savedsizes.filters).some(v => selected.multiSelects[v]);
      let content = null;

      if (!isCustomer) {
        content = (
          <>
          Login to see saved sizes <button type="button" onClick={onSaveFeatureToggle}>Sign In</button>
          </>
        );
      } else if (savedsizes.id && showSavedToggle && hasSavedValues) {
        const toggleText = applySavedFilters ? 'off' : 'on';
        content = (
          <>
          <button
            id="toggleBtn"
            className={css.toggleBtn}
            type="button"
            aria-pressed={applySavedFilters}
            onClick={onSaveFeatureToggle}/>
          <label htmlFor="toggleBtn">Turn {toggleText} auto-apply{makeSavedFiltersToggleText()}</label>
          </>
        );
      } else if (showSavedToggle && !hasSavedValues && !hasChosenASaveFilter) {
        content = 'Select a size you want to save for future searches';
      }

      if (content) {
        return (
          <div className={css.toggleTop}>
            {content}
          </div>
        );
      }
    }
    return null;
  };

  const makeFacetGroupValues = (facetGroup, i, isToggled, section, isColorFacet) => {
    const { facetField, facetFieldDisplayName, selectedRange, values } = facetGroup;
    const shouldRenderAutoComplete = hasAutoComplete && facetGroup.values.length > autoCompleteMinValues;
    const relType = NOFOLLOW_FACET_RE.test(facetGroup.facetField) ? 'nofollow' : undefined;
    const facetValues = (shouldRenderAutoComplete && autoComplete?.[facetField]?.values) || values;
    let facetValueMarkup = facetValues.map((facet, j) => {
      const facetSelected = facetField === BEST_FOR_YOU_FACETFIELD ? filters.personalizedSize?.facets?.[0]?.selected : facet.isSelected;
      let displayValue = (
        <a
          rel={facet.facetUrl ? relType : undefined}
          tabIndex={isToggled ? -1 : 0}
          aria-label={`${facetSelected ? 'Disable' : 'Enable'} filter for ${facetFieldDisplayName} ${facet.displayName}, ${facet.count} products available`}
          href={facet.facetUrl}
          onKeyPress={handleFacetSelect.bind(this, facetField, facet.name, i, j, facetSelected, section)}
          onClick={handleFacetSelect.bind(this, facetField, facet.name, i, j, facetSelected, section)}
          data-test-id={testId('facetBox')}>
          <span data-test-id={testId('facetName')}>
            {facet.displayName === 'One Size' ? 'OS' : facet.displayName}
          </span>
          {!!facet.count && <span className={css.facetCount}>({facet.count})</span>}
        </a>
      );

      if (isColorFacet) {
        const colorName = facet.name.toLowerCase();
        const color = COLOR_SWATCH_MAP[colorName] || 'unknown';
        displayValue = (
          <a
            rel={facet.facetUrl ? relType : undefined}
            tabIndex={isToggled ? -1 : 0}
            aria-label={`${facetSelected ? 'Disable' : 'Enable'} filter for ${facetFieldDisplayName} ${facet.displayName}, ${facet.count} products available`}
            title={capitalize(colorName)}
            href={facet.facetUrl}
            onKeyPress={handleFacetSelect.bind(this, facetField, facet.name, i, j, facetSelected, section)}
            onClick={handleFacetSelect.bind(this, facetField, facet.name, i, j, facetSelected, section)}
            data-test-id={testId('colorFacet')}>
            <div className={cn({ [css.unknown]: color === 'unknown' })} data-test-id={testId('colorFacet')} style={{ background: color }}/>
          </a>
        );
      }

      return (
        <li key={facet.name} className={cn({ [css.selectedFacet]: facetSelected })} data-test-id={testId(`facet-${facet.displayName}`)}>
          {displayValue}
        </li>
      );
    });

    if (facetField === PRICE_RANGE_FACET_NAME) {
      facetValueMarkup = <PriceSlider
        isToggled={isToggled}
        minDefault={selectedRange?.min}
        maxDefault={selectedRange?.max}
      />;
    }

    return (
      <div
        className={css.facetList}
        aria-hidden={isToggled}
        role="group"
        data-test-id={testId(`${facetGroup.facetFieldDisplayName}-values`)}>
        <ul role="menu">
          {facetValueMarkup}
        </ul>
      </div>
    );
  };

  const autoCompleteHandler = (value, searchText) => value.displayName.toLowerCase().includes(searchText.toLowerCase());

  const makeAutoComplete = facetGroup => {
    if (hasAutoComplete && facetGroup.values.length > autoCompleteMinValues) {
      return (
        <div className={css.autoComplete}>
          <FacetAutoComplete
            id={facetGroup.facetField}
            title={facetGroup.facetFieldDisplayName}
            handler={autoCompleteHandler}
            values={facetGroup.values}
            hydraNewSearchNav={hydraNewSearchNav} />
        </div>
      );
    }
  };

  const makeTrustedRetailerMessaging = () => {
    const { navigation } = facets;
    const { trustedRetailers, list } = products;
    const { sizing, core, zEverythingElse } = navigation;
    if (trustedRetailers.length && (!sizing.length && !core.length && !zEverythingElse.length || list.length)) {
      return (
        <div className={css.crossSiteMessaging}>
          <strong>Can't find a filter?</strong>
          <p>It may be missing for products marked with:</p>
          <div className={css.trustedBanner}><TrustedRetailerBanner/></div>
        </div>
      );
    }
  };

  const makeFacetGroups = (facetGroup, index, section = '') => {
    if (facetGroup) {
      const isDesktopToggled = section !== 'sizing' && facetGroup.desktopSelected;
      const hasAutoComplete = section !== 'sizing' && facetGroup.values.length > autoCompleteMinValues;
      const isColorFacet = facetGroup.facetField === 'colorFacet';
      const isBrandFacet = facetGroup.facetField === 'brandNameFacet';
      const showMoreLessText = `See ${!facetGroup.showMore ? 'More' : 'Less'}`;
      return (
        <section
          key={`${facetGroup.facetField}`}
          className={cn(
            css.facetGroup,
            {
              [css.desktopSelected]: isDesktopToggled,
              [css.hasAutoComplete]: hasAutoComplete,
              [css.facetBox]: facetGroup.symbolicSizingField,
              [css.hasCheckbox]: !facetGroup.symbolicSizingField && !isColorFacet,
              [css.colorFacet]: isColorFacet,
              [css.brandFacet]: isBrandFacet,
              [css.showMore]: facetGroup.showMore
            })}
          data-test-id={testId('facetGroup')}>
          {renderFacetHead(facetGroup, isDesktopToggled, section)}
          {hasSaveFilters && makeSaveSizeFeature(facetGroup.facetField, facetGroup.facetFieldDisplayName)}
          {hasAutoComplete && makeAutoComplete(facetGroup)}
          {makeFacetGroupValues(facetGroup, index, isDesktopToggled, section, isColorFacet)}
          { !isDesktopToggled && section !== 'sizing' &&
          facetGroup.values.length > 10 && (
            <button type="button" className={cn(css.seeMoreLess, { [css.isLess]: !facetGroup.showMore })} onClick={toggleShowMore.bind(this, facetGroup.facetField, index, section)}>
              {showMoreLessText}
            </button>
          )}
        </section>
      );
    }
  };

  const sizingGroupToggle = () => {
    const { sizingFacetGroupToggled } = facets;
    if (hydraPills) {
      // previous value before state update
      const isOpen = !sizingFacetGroupToggled;

      track(() => ([
        evGroupedPillToggle, {
          facetGroup: 'sizing',
          facetClickSource: 'FACET_PILLS_DROPDOWN',
          collapse: isOpen
        }
      ]));
    }

    toggleSizingFacetGroup();
  };

  const makeFacets = () => {
    const sortFacet = <NewSort
      className={css.mobileSortFacet}
      key="sortKey"
      sortOptions={sortOptions}
      onSortSelected={onSortSelected}
      selectedSort={filters.sort}
      filters={filters}
      testId={testId('selectSortValue')}
      compact={true}
      makePersonalizedSortToggle={makePersonalizedSortToggle} />;

    const navFacets = Object.keys(facets.navigation).map(section => {
      const facetGroupMarkup = facets.navigation[section].map((facetGroup, i) => makeFacetGroups(facetGroup, i, section));
      const isToggled = facets.sizingFacetGroupToggled;
      const sizingFacetGroupMarkup = (
        <section key={section} className={cn(css.facetGroup, css.sizingGroup, { [css.desktopSelected]: isToggled })}>
          <h3 className={css.facetGroupName}>
            <button
              className={css.facetGroupHead}
              onClick={sizingGroupToggle}
              type="button"
              data-test-id-facet-head-name={testId('Sizing')}
              data-open={!isToggled}
              aria-expanded={!isToggled}>Sizing</button>
          </h3>
          <div className={css.facetList}>{facetGroupMarkup}</div>
        </section>
      );
      return section === 'sizing' ? !!facets.navigation.sizing.length && sizingFacetGroupMarkup : facetGroupMarkup;
    });
    return [sortFacet, ...navFacets];
  };

  return (
    <div className={cn(css.facetStyle)}>
      {(facetHeader && !!facets.toDisplay?.length) && <h2 className={css.desktopFacetsHeader}>{facetHeader}</h2>}
      {hydraCrossSiteSelling && makeTrustedRetailerMessaging()}
      {mobileFacetSort && (
        <section className={cn(css.facetGroup, css.mobileSortFacet, { [css.selected]: facets.mobileFacetSortToggled })}>
          <h3>
            <button
              type="button"
              className={css.facetGroupHead}
              aria-expanded={facets.mobileFacetSortToggled}
              data-selected-facet-group-name="sort"
              onClick={onFacetGroupSelect}>
              Sort
            </button>
          </h3>
          <div
            className={css.facetList}
            aria-hidden={facets.mobileFacetSortToggled}
            role="group">
            <Sort accordion={true}/>
          </div>
        </section>)}
      {hasSaveFilters && makeSavedFiltersTop()}
      {makeFacets()}
    </div>
  );
});

NewFacets.displayName = 'NewFacets';

NewFacets.propTypes = {
  facets: PropTypes.object.isRequired,
  onFacetGroupSelect: PropTypes.func.isRequired,
  onFacetSelect: PropTypes.func.isRequired
};

const NewFacetsWithErrorBoundary = withErrorBoundary('NewFacets', NewFacets);
export default NewFacetsWithErrorBoundary;
